///
/// EarthBrowser
///
/// Created by Matt Giger on 9/20/2026
/// Copyright (c) 2026 Geodata Labs
///
/// Rewrites public B2 dataset URLs onto the tile-server dataset proxy
/// (tiles.earthbrowser.com/data → tile-server/src/proxy.ts datasetProxy)
/// so dataset traffic moves off direct B2 access and the bucket can
/// eventually be restricted to Cloudflare-only. Applied at fetch time
/// because manifests and Arrow rows carry absolute B2 URLs (manifestUrl,
/// shard.url, image_url, thumb_url, ...) that override any relative-path
/// construction. Non-dataset URLs pass through untouched (static assets
/// use Constants.baseStaticUrl directly; see tile-server staticProxy).
///
/// Also rewrites the ebpackages bucket (user-uploaded datasets, curated
/// GeoPackages, avatars) onto the /packages passthrough. api-server now
/// issues those cdn_urls on the passthrough directly (B2_URL_PREFIX), but
/// URLs minted before the switch persist in story dataset refs, the
/// GeoPackage catalog and profile avatars, and the local api-server
/// (.dev.vars) still falls back to the B2 host.
///
/// In dev the Vite /b2-proxy route is used instead; it forwards to the
/// tile-server passthroughs (direct B2 access is closed).
///

import { Constants } from "@/constants"

const cB2Host = "https://b2.earthbrowser.com"
const cB2FilePrefix = `${cB2Host}/file/earthbrowser/`
const cB2DatasetsPrefix = `${cB2FilePrefix}datasets/`
const cB2PackagesPrefix = `${cB2Host}/file/ebpackages/`

// Bucket-root media prefixes (frame imagery referenced by dataset Arrow rows
// but published outside the datasets tree: hourly cloud composites,
// SST/IMERG/MRMS frames, the radar equirect mosaic, NEXRAD atlases). Served
// by the tile-server /media passthrough; must match cMediaPrefixes in
// tile-server/src/proxy.ts.
const cB2MediaPrefixes = /^(clouds|storm-clouds|cloud-mosaic|sst|imerg|mrms|radar|nexrad|wildfires)\//

/** Rewrite a B2 datasets, bucket-root media or ebpackages URL to the tile-server proxy (or dev proxy) */
export function datasetUrl(url: string): string {
	if (url.startsWith(cB2PackagesPrefix)) {
		if (import.meta.env.DEV) {
			return url.replace(cB2Host, "/b2-proxy")
		}
		return `${Constants.basePackagesUrl}/${url.slice(cB2PackagesPrefix.length)}`
	}
	if (url.startsWith(cB2DatasetsPrefix)) {
		if (import.meta.env.DEV) {
			return url.replace(cB2Host, "/b2-proxy")
		}
		return `${Constants.baseDataUrl}/${url.slice(cB2DatasetsPrefix.length)}`
	}
	if (url.startsWith(cB2FilePrefix) && cB2MediaPrefixes.test(url.slice(cB2FilePrefix.length))) {
		if (import.meta.env.DEV) {
			return url.replace(cB2Host, "/b2-proxy")
		}
		return `${Constants.baseMediaUrl}/${url.slice(cB2FilePrefix.length)}`
	}
	return url
}
